import { FrameworkInfo } from '../cli/utils/framework-detector';
import { EMBEDDED_TEMPLATES } from './embedded-templates';

export interface TemplateOptions {
  framework: FrameworkInfo;
  typescript?: boolean;
  features?: string[];
  apiPath?: string;
  auth?: boolean;
  rateLimit?: boolean;
  cors?: boolean;
  scanPaths?: string[];
  include?: string[];
  exclude?: string[];
}

export interface GeneratedFile {
  path: string;
  content: string;
  type: 'route' | 'middleware' | 'util' | 'config';
}

export class TemplateGenerator {
  private templates: Map<string, string> = new Map();
  
  constructor() {
    this.loadTemplates();
  }
  
  private loadTemplates() {
    // Use embedded templates first, fall back to file system if needed
    for (const [templateKey, templateContent] of Object.entries(EMBEDDED_TEMPLATES)) {
      this.templates.set(templateKey, templateContent);
    }
    
    // If embedded templates are empty (dev mode), try loading from file system
    if (this.templates.size === 0) {
      console.warn('No embedded templates found, attempting to load from file system...');
      // Note: loadTemplatesFromFileSystem is now async but we can't await here
      // This is only for dev mode, so we'll handle it separately
    }
  }
  
  private async loadTemplatesFromFileSystem() {
    // Dynamic import to avoid bundling fs modules in browser
    const { readFileSync } = await import('fs');
    const { join } = await import('path');
    
    const templateDir = join(__dirname, '../templates');
    
    const templates = [
      'app-router/tasks-stream.ts',
      'app-router/ai-chat.ts',
      'app-router/files.ts',
      'app-router/tasks.ts',
      'pages-router/tasks-stream.ts',
      'pages-router/ai-chat.ts',
      'pages-router/files.ts',
      'pages-router/tasks.ts',
      'middleware/auth.ts',
      'middleware/auth-pages.ts',
      'utils/error-handler.ts',
      'utils/cors.ts',
      'utils/rate-limit.ts',
    ];
    
    for (const template of templates) {
      try {
        const content = readFileSync(join(templateDir, template), 'utf-8');
        this.templates.set(template, content);
      } catch (error) {
        console.warn(`Template ${template} not found, using inline version`);
      }
    }
  }
  
  generateApiRoutes(options: TemplateOptions): GeneratedFile[] {
    const files: GeneratedFile[] = [];
    const isAppRouter = options.framework.isAppRouter;
    const ext = options.typescript !== false ? 'ts' : 'js';
    const apiBase = this.getApiPath(options);
    
    // Generate route files
    const routes = [
      { name: 'tasks-stream', path: 'tasks/stream' },
      { name: 'tasks', path: 'tasks' },
      { name: 'files', path: 'files' },
    ];
    
    // Add AI chat if feature is enabled
    if (options.features?.includes('ai')) {
      routes.push({ name: 'ai-chat', path: 'ai/chat' });
    }
    
    for (const route of routes) {
      const templateKey = isAppRouter 
        ? `app-router/${route.name}.ts`
        : `pages-router/${route.name}.ts`;
        
      let content = this.templates.get(templateKey) || this.getInlineTemplate(route.name, isAppRouter);
      
      // Apply transformations
      content = this.applyMiddleware(content, options);
      content = this.convertToJavaScript(content, options);
      
      const fileName = isAppRouter ? `route.${ext}` : `index.${ext}`;
      const filePath = this.joinPath(apiBase, 'vibeship', route.path, fileName);
      
      files.push({
        path: filePath,
        content,
        type: 'route',
      });
    }
    
    return files;
  }
  
  generateMiddleware(options: TemplateOptions): GeneratedFile[] {
    const files: GeneratedFile[] = [];
    const ext = options.typescript !== false ? 'ts' : 'js';
    const base = options.framework.hasSrcDir ? 'src' : '';
    
    // Auth middleware
    if (options.auth) {
      const authTemplate = options.framework.isAppRouter
        ? this.templates.get('middleware/auth.ts') || this.getInlineMiddleware('auth')
        : this.templates.get('middleware/auth-pages.ts') || this.getInlineMiddleware('auth-pages');
        
      files.push({
        path: this.joinPath(base, 'middleware', `auth.${ext}`),
        content: this.convertToJavaScript(authTemplate, options),
        type: 'middleware',
      });
    }
    
    // Error handler
    const errorHandler = this.templates.get('utils/error-handler.ts') || this.getInlineMiddleware('error-handler');
    files.push({
      path: this.joinPath(base, 'utils', `error-handler.${ext}`),
      content: this.convertToJavaScript(errorHandler, options),
      type: 'util',
    });
    
    // CORS
    if (options.cors) {
      const corsTemplate = this.templates.get('utils/cors.ts') || this.getInlineMiddleware('cors');
      files.push({
        path: this.joinPath(base, 'utils', `cors.${ext}`),
        content: this.convertToJavaScript(corsTemplate, options),
        type: 'util',
      });
    }
    
    // Rate limiting
    if (options.rateLimit) {
      const rateLimitTemplate = this.templates.get('utils/rate-limit.ts') || this.getInlineMiddleware('rate-limit');
      files.push({
        path: this.joinPath(base, 'utils', `rate-limit.${ext}`),
        content: this.convertToJavaScript(rateLimitTemplate, options),
        type: 'util',
      });
    }
    
    // Config wrapper for API routes
    const configWrapper = this.generateConfigWrapper(options);
    const libBase = options.framework.hasSrcDir ? 'src' : '';
    files.push({
      path: this.joinPath(libBase, 'lib', `vibeship-config.${ext}`),
      content: configWrapper,
      type: 'util',
    });

    // API error handling utilities
    const apiErrors = this.templates.get('utils/api-errors.ts') || this.getInlineUtility('api-errors');
    files.push({
      path: this.joinPath(libBase, 'utils', `api-errors.${ext}`),
      content: this.convertToJavaScript(apiErrors, options),
      type: 'util',
    });

    // Fallback handlers
    const fallbackHandlers = this.templates.get('utils/fallback-handlers.ts') || this.getInlineUtility('fallback-handlers');
    files.push({
      path: this.joinPath(libBase, 'utils', `fallback-handlers.${ext}`),
      content: this.convertToJavaScript(fallbackHandlers, options),
      type: 'util',
    });

    // Health check endpoint (optional)
    if (options.features?.includes('health-check')) {
      const healthCheck = this.templates.get('app-router/health.ts') || this.getInlineUtility('health-check');
      const apiBase = this.getApiPath(options);
      files.push({
        path: this.joinPath(apiBase, 'vibeship', 'health', `route.${ext}`),
        content: this.convertToJavaScript(healthCheck, options),
        type: 'route',
      });
    }
    
    return files;
  }
  
  generateConfigFile(options: TemplateOptions): GeneratedFile {
    const ext = options.typescript !== false ? 'ts' : 'js';
    
    const configContent = `import { defineConfig } from '@vibeship/devtools/config';

export default defineConfig({
  // Paths to scan for markdown files
  scanPaths: ${JSON.stringify(options.scanPaths || ['./docs', './README.md', './.tickets'], null, 2).replace(/\n/g, '\n  ')},
  
  // File patterns
  include: ${JSON.stringify(options.include || ['**/*.md', '**/*.mdx'], null, 2).replace(/\n/g, '\n  ')},
  exclude: ${JSON.stringify(options.exclude || ['**/node_modules/**', '**/.next/**'], null, 2).replace(/\n/g, '\n  ')},
  
  // Features
  features: {
    tasks: true,
    ai: ${options.features?.includes('ai') ?? false},
    realtime: true,
    commandPalette: true,
  },
  
  // Security
  security: {
    authentication: ${options.auth ?? false},
    rateLimit: ${options.rateLimit ?? false},
    cors: ${options.cors ?? false},
    allowedPaths: ['**/*'],
  },
  
  // Task patterns
  taskPatterns: {
    todo: true,
    fixme: true,
    hack: true,
    note: true,
    bug: true,
    optimize: true,
    refactor: true,
  },
  
  // UI configuration
  ui: {
    theme: 'system',
    position: 'bottom-right',
    defaultSize: {
      width: '400px',
      height: '600px',
    },
    hotkey: 'cmd+k',
    showInProduction: false,
  },
  
  // API configuration
  api: {
    basePath: '/api/vibeship',
    version: 'v1',
    documentation: true,
    timeout: 30000,
  },
  
  // Cache configuration
  cache: {
    enabled: true,
    directory: '.vibecode/cache',
    maxSize: 100,
    ttl: 300000, // 5 minutes
    strategy: 'lru',
  },
});`;
    
    return {
      path: `vibeship.config.${ext}`,
      content: options.typescript === false 
        ? this.convertConfigToJS(configContent)
        : configContent,
      type: 'config',
    };
  }
  
  private getApiPath(options: TemplateOptions): string {
    if (options.apiPath) return options.apiPath;
    
    const base = options.framework.hasSrcDir ? 'src' : '';
    return options.framework.isAppRouter
      ? this.joinPath(base, 'app', 'api')
      : this.joinPath(base, 'pages', 'api');
  }
  
  private joinPath(...parts: string[]): string {
    return parts.filter(Boolean).join('/');
  }
  
  private applyMiddleware(content: string, options: TemplateOptions): string {
    let modified = content;
    
    // Only apply middleware for App Router
    if (!options.framework.isAppRouter) {
      return modified;
    }
    
    // Determine which middleware to apply
    const needsRateLimit = options.rateLimit;
    const needsAuth = options.auth;
    
    if (!needsRateLimit && !needsAuth) {
      // No middleware needed, return as is
      return modified;
    }
    
    // Add necessary imports at the top
    const imports: string[] = [];
    imports.push(`import { withErrorHandler } from '@/utils/error-handler';`);
    if (needsRateLimit) {
      imports.push(`import { withRateLimit } from '@/utils/rate-limit';`);
    }
    if (needsAuth) {
      imports.push(`import { withAuth } from '@/middleware/auth';`);
    }
    
    // Process each exported function individually
    const functionPattern = /export\s+async\s+function\s+(GET|POST|PUT|DELETE|PATCH)\s*\((.*?)\)\s*(?::\s*Promise<Response>)?\s*\{/gs;
    const functions: Array<{name: string, params: string, startIndex: number, endIndex?: number}> = [];
    
    // Find all function declarations
    let match;
    while ((match = functionPattern.exec(modified)) !== null) {
      functions.push({
        name: match[1],
        params: match[2],
        startIndex: match.index,
      });
    }
    
    // Find the end index of each function by tracking brace depth
    for (let i = 0; i < functions.length; i++) {
      const func = functions[i];
      let braceDepth = 0;
      let inString = false;
      let stringChar = '';
      let inTemplateLiteral = false;
      
      // Start searching from the opening brace of the function
      const openBraceIndex = modified.indexOf('{', func.startIndex);
      if (openBraceIndex === -1) {
        console.error(`Could not find opening brace for function ${func.name}`);
        continue;
      }
      
      // Process character by character
      for (let j = openBraceIndex; j < modified.length; j++) {
        const char = modified[j];
        const prevChar = j > 0 ? modified[j - 1] : '';
        
        // Skip escaped characters
        if (prevChar === '\\') {
          continue;
        }
        
        // Handle template literals
        if (char === '`' && !inString) {
          inTemplateLiteral = !inTemplateLiteral;
          continue;
        }
        
        // Handle regular strings
        if ((char === '"' || char === "'") && !inTemplateLiteral) {
          if (!inString) {
            inString = true;
            stringChar = char;
          } else if (char === stringChar) {
            inString = false;
          }
          continue;
        }
        
        // Count braces only when not in strings or template literals
        if (!inString && !inTemplateLiteral) {
          if (char === '{') {
            braceDepth++;
          } else if (char === '}') {
            braceDepth--;
            if (braceDepth === 0) {
              func.endIndex = j;
              break;
            }
          }
        }
      }
      
      if (func.endIndex === undefined) {
        console.error(`Could not find closing brace for function ${func.name}. Brace depth: ${braceDepth}`);
        // Mark this function as invalid so we skip transformation
        func.endIndex = -1;
      }
    }
    
    // Build the wrapper for each function
    const buildWrapper = (hasRateLimit: boolean, hasAuth: boolean): string => {
      if (hasRateLimit && hasAuth) {
        return 'withRateLimit(withAuth(async (';
      } else if (hasRateLimit) {
        return 'withRateLimit(async (';
      } else if (hasAuth) {
        return 'withAuth(async (';
      }
      return 'async (';
    };
    
    const buildClosing = (hasRateLimit: boolean, hasAuth: boolean): string => {
      if (hasRateLimit && hasAuth) {
        return '}));';
      } else if (hasRateLimit || hasAuth) {
        return '});';
      }
      return '}';
    };
    
    // Apply transformations in reverse order to preserve indices
    for (let i = functions.length - 1; i >= 0; i--) {
      const func = functions[i];
      if (func.endIndex === undefined || func.endIndex === -1) {
        console.warn(`Skipping middleware transformation for function ${func.name} due to parsing error`);
        continue;
      }
      
      // Replace the closing brace
      const closing = buildClosing(!!needsRateLimit, !!needsAuth);
      modified = modified.slice(0, func.endIndex) + closing + modified.slice(func.endIndex + 1);
      
      // Replace the function declaration
      const wrapper = buildWrapper(!!needsRateLimit, !!needsAuth);
      const replacement = `export const ${func.name} = ${wrapper}${func.params}) => {`;
      
      // Find the actual end of the function declaration (the opening brace)
      const openBraceIndex = modified.indexOf('{', func.startIndex);
      modified = modified.slice(0, func.startIndex) + replacement + modified.slice(openBraceIndex + 1);
    }
    
    // Add imports at the beginning
    modified = imports.join('\n') + '\n' + modified;
    
    return modified;
  }
  
  private convertToJavaScript(content: string, options: TemplateOptions): string {
    if (options.typescript !== false) return content;
    
    return content
      // Remove type imports
      .replace(/import type .* from ['"].*['"];?\n/g, '')
      // Remove type annotations
      .replace(/: NextRequest/g, '')
      .replace(/: NextResponse/g, '')
      .replace(/: NextApiRequest/g, '')
      .replace(/: NextApiResponse/g, '')
      .replace(/: Promise<Response>/g, '')
      .replace(/: Promise<void>/g, '')
      .replace(/: string/g, '')
      .replace(/: number/g, '')
      .replace(/: boolean/g, '')
      .replace(/: any/g, '')
      // Remove interface declarations
      .replace(/interface \w+ {[^}]+}/gs, '')
      // Remove type declarations
      .replace(/type \w+ = [^;]+;/g, '')
      // Remove generic types
      .replace(/<[^>]+>/g, '');
  }
  
  private convertConfigToJS(content: string): string {
    return content.replace(
      "import { defineConfig } from '@vibeship/devtools/config';",
      "const { defineConfig } = require('@vibeship/devtools/config');"
    ).replace(
      'export default defineConfig',
      'module.exports = defineConfig'
    );
  }
  
  private getInlineTemplate(name: string, isAppRouter: boolean): string {
    // Fallback inline templates
    const templates: Record<string, string> = {
      'tasks-stream': `// Task streaming endpoint
export async function GET(request${isAppRouter ? ': NextRequest' : ''}) {
  return new Response('Task streaming endpoint - implement SSE logic here');
}`,
      'tasks': `// Task scanning endpoint
export async function GET(request${isAppRouter ? ': NextRequest' : ''}) {
  return ${isAppRouter ? 'NextResponse' : 'res'}.json({ tasks: [] });
}`,
      'files': `// File operations endpoint
export async function GET(request${isAppRouter ? ': NextRequest' : ''}) {
  return ${isAppRouter ? 'NextResponse' : 'res'}.json({ files: [] });
}`,
      'ai-chat': `// AI chat endpoint
export async function POST(request${isAppRouter ? ': NextRequest' : ''}) {
  return ${isAppRouter ? 'NextResponse' : 'res'}.json({ message: 'AI chat endpoint' });
}`,
    };
    
    return templates[name] || '// Template not found';
  }

  private getInlineMiddleware(name: string): string {
    const templates: Record<string, string> = {
      'auth': `// Authentication middleware
export function withAuth(handler: Function) {
  return async function(req: any, ...args: any[]) {
    // Add authentication logic here
    return handler(req, ...args);
  };
}`,
      'auth-pages': `// Authentication middleware for Pages Router
export function withAuth(handler: Function) {
  return async function(req: any, res: any) {
    // Add authentication logic here
    return handler(req, res);
  };
}`,
      'error-handler': `// Error handling utility
export function withErrorHandler(handler: Function) {
  return async function(...args: any[]) {
    try {
      return await handler(...args);
    } catch (error) {
      console.error('API Error:', error);
      throw error;
    }
  };
}`,
      'cors': `// CORS utility
export function setupCors() {
  return {
    'Access-Control-Allow-Origin': '*',
    'Access-Control-Allow-Methods': 'GET, POST, PUT, DELETE, OPTIONS',
    'Access-Control-Allow-Headers': 'Content-Type, Authorization',
  };
}`,
      'rate-limit': `// Rate limiting utility
export function withRateLimit(handler: Function) {
  return async function(...args: any[]) {
    // Add rate limiting logic here
    return handler(...args);
  };
}`,
    };
    
    return templates[name] || '// Middleware template not found';
  }

  private getInlineUtility(name: string): string {
    const templates: Record<string, string> = {
      'api-errors': `// Basic API error handling
export class ApiError extends Error {
  constructor(public code: string, public statusCode: number, public details: any) {
    super(details.message);
  }
  
  toResponse() {
    return new Response(JSON.stringify({
      error: {
        code: this.code,
        ...this.details
      }
    }), { status: this.statusCode });
  }
}

export const ErrorCodes = {
  INTERNAL_ERROR: 'INTERNAL_ERROR',
  CONFIG_NOT_FOUND: 'CONFIG_NOT_FOUND',
  INVALID_REQUEST: 'INVALID_REQUEST'
};

export function getRequestId() {
  return 'req_' + Date.now();
}

export async function withErrorHandling(fn: Function, requestId: string) {
  try {
    return await fn();
  } catch (error) {
    throw new ApiError(ErrorCodes.INTERNAL_ERROR, 500, {
      message: 'Internal error occurred',
      requestId
    });
  }
}`,

      'fallback-handlers': `// Basic fallback configuration
export function getDefaultConfig() {
  return {
    scanPaths: ['./src', './app', './docs'],
    include: ['**/*.md', '**/*.tsx', '**/*.ts'],
    exclude: ['**/node_modules/**'],
    features: { tasks: true, ai: false }
  };
}

export async function loadConfigSafely() {
  try {
    const { getConfig } = await import('@/lib/vibeship-config');
    return await getConfig();
  } catch {
    return getDefaultConfig();
  }
}

export async function loadDependenciesWithFallbacks() {
  const config = await loadConfigSafely();
  return {
    config,
    Logger: () => ({ info: console.log, error: console.error, warn: console.warn }),
    FileScanner: () => ({ scan: async () => [] }),
    TaskExtractor: () => ({ extract: async () => [] }),
    CacheManager: () => ({ get: () => null, set: () => {}, delete: () => {} })
  };
}`,

      'health-check': `// Basic health check
import { NextRequest, NextResponse } from 'next/server';

export async function GET() {
  return NextResponse.json({
    status: 'healthy',
    timestamp: new Date().toISOString(),
    version: '1.0.37'
  });
}`
    };
    
    return templates[name] || '// Utility template not found';
  }
  
  private generateConfigWrapper(options: TemplateOptions): string {
    const ext = options.typescript !== false ? 'ts' : 'js';
    const isTS = ext === 'ts';
    
    // Config file is always in the root, so we need relative imports from lib/
    const configPath = options.framework.hasSrcDir ? '../../vibeship.config' : '../vibeship.config';
    
    // Generate the content based on language
    if (isTS) {
      return this.generateTypeScriptConfigWrapper(configPath, options);
    } else {
      return this.generateJavaScriptConfigWrapper(configPath, options);
    }
  }

  private generateTypeScriptConfigWrapper(configPath: string, options: TemplateOptions): string {
    return `import type { TaskConfig } from '@vibeship/devtools/server';

let cachedConfig: TaskConfig | null = null;

export async function getConfig(): Promise<TaskConfig> {
  if (cachedConfig) return cachedConfig;
  
  // Try to load from vibeship.config.ts
  try {
    const vibeshipConfig = await import('${configPath}');
    cachedConfig = vibeshipConfig.default || vibeshipConfig;
    return cachedConfig;
  } catch (error) {
    // Return defaults if no config file
    return getDefaultConfig();
  }
}

export function getDefaultConfig(): TaskConfig {
  return {
    scanPaths: ${JSON.stringify(options.scanPaths || ['./src', './app', './docs', './README.md'], null, 4)},
    include: ${JSON.stringify(options.include || ['**/*.md', '**/*.mdx', '**/*.tsx', '**/*.ts'], null, 4)},
    exclude: ${JSON.stringify(options.exclude || ['**/node_modules/**', '**/.next/**', '**/dist/**'], null, 4)},
    taskPatterns: {
      todo: true,
      fixme: true,
      hack: true,
      note: true,
      bug: true,
      optimize: true,
      refactor: true,
    },
    defaultPhase: 'Backlog',
    defaultMilestone: 'Unassigned',
    cacheEnabled: true,
    cacheTTL: 300000, // 5 minutes
    maxFileSize: 1048576, // 1MB
    maxDepth: 5,
    features: {
      tasks: true,
      ai: ${options.features?.includes('ai') ?? false},
      realtime: true,
      commandPalette: true,
    },
    security: {
      authentication: ${options.auth ?? false},
      rateLimit: ${options.rateLimit ?? false},
      cors: ${options.cors ?? false},
      allowedPaths: ['**/*'],
    },
    ui: {
      theme: 'system',
      position: 'bottom-right',
      defaultSize: {
        width: '400px',
        height: '600px',
      },
      hotkey: 'cmd+k',
      showInProduction: false,
    },
    api: {
      basePath: '/api/vibeship',
      version: 'v1',
      documentation: true,
      timeout: 30000,
    },
    cache: {
      enabled: true,
      directory: '.vibecode/cache',
      maxSize: 100,
      ttl: 300000,
      strategy: 'lru',
    },
  };
}
`;
  }

  private generateJavaScriptConfigWrapper(configPath: string, options: TemplateOptions): string {
    return `let cachedConfig = null;

async function getConfig() {
  if (cachedConfig) return cachedConfig;
  
  // Try to load from vibeship.config.js
  try {
    const vibeshipConfig = require('${configPath}');
    cachedConfig = vibeshipConfig.default || vibeshipConfig;
    return cachedConfig;
  } catch (error) {
    // Return defaults if no config file
    return getDefaultConfig();
  }
}

function getDefaultConfig() {
  return {
    scanPaths: ${JSON.stringify(options.scanPaths || ['./src', './app', './docs', './README.md'], null, 4)},
    include: ${JSON.stringify(options.include || ['**/*.md', '**/*.mdx', '**/*.tsx', '**/*.ts'], null, 4)},
    exclude: ${JSON.stringify(options.exclude || ['**/node_modules/**', '**/.next/**', '**/dist/**'], null, 4)},
    taskPatterns: {
      todo: true,
      fixme: true,
      hack: true,
      note: true,
      bug: true,
      optimize: true,
      refactor: true,
    },
    defaultPhase: 'Backlog',
    defaultMilestone: 'Unassigned',
    cacheEnabled: true,
    cacheTTL: 300000, // 5 minutes
    maxFileSize: 1048576, // 1MB
    maxDepth: 5,
    features: {
      tasks: true,
      ai: ${options.features?.includes('ai') ?? false},
      realtime: true,
      commandPalette: true,
    },
    security: {
      authentication: ${options.auth ?? false},
      rateLimit: ${options.rateLimit ?? false},
      cors: ${options.cors ?? false},
      allowedPaths: ['**/*'],
    },
    ui: {
      theme: 'system',
      position: 'bottom-right',
      defaultSize: {
        width: '400px',
        height: '600px',
      },
      hotkey: 'cmd+k',
      showInProduction: false,
    },
    api: {
      basePath: '/api/vibeship',
      version: 'v1',
      documentation: true,
      timeout: 30000,
    },
    cache: {
      enabled: true,
      directory: '.vibecode/cache',
      maxSize: 100,
      ttl: 300000,
      strategy: 'lru',
    },
  };
}

module.exports = { getConfig, getDefaultConfig };
`;
  }

  async writeFiles(files: GeneratedFile[]): Promise<void> {
    // Dynamic import to avoid bundling fs modules in browser
    const { writeFileSync, mkdirSync, existsSync } = await import('fs');
    const { dirname } = await import('path');
    
    for (const file of files) {
      const dir = dirname(file.path);
      if (!existsSync(dir)) {
        mkdirSync(dir, { recursive: true });
      }
      writeFileSync(file.path, file.content);
    }
  }
}